import { Fragment, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronDown,
  IconChevronRight,
  IconChevronsUpDown,
  IconDownload,
  IconX,
} from '../icons';
import { useTranslate } from '../i18n/react';
import type { TableStatus } from './Table';

/**
 * A report grid that pivots.
 *
 * <p>Like {@link Table} it only draws — the screen fetches, filters and hands it rows. What it
 * adds is grouping done the way a spreadsheet does it: the reader drags a column header into the
 * bar above the grid and the rows fold under it, with a subtotal on every group and a grand
 * total at the foot. Drop a second header and it nests; a grouped column leaves the grid, because
 * once every row in a group shares that value, a column repeating it is noise.</p>
 *
 * <p>How a numeric column rolls up is the reader's to choose — sum, average or count — from a
 * control in its header, because the question "how many days overdue" wants a total on one screen
 * and an average on the next, and the report should not have to pick for them.</p>
 */

export type GridAgg = 'sum' | 'avg' | 'count';

export interface GridColumn<T> {
  id: string;
  header: string;
  align?: 'left' | 'right';
  /** `is-muted` for secondary text, `is-primary` for the column the report is about. */
  className?: string;
  cell: (row: T) => ReactNode;
  /** When set, the reader may group by this column; the string is the group a row falls in. */
  groupValue?: (row: T) => string;
  /** When set, the column is numeric and the reader chooses how it rolls up. */
  value?: (row: T) => number | null;
  /** The key the column sorts on; falls back to `value` then `groupValue`. */
  sortValue?: (row: T) => string | number | null;
  /**
   * When set, the column gets a header filter on this value. `text` keeps rows whose value
   * contains what is typed; `select` offers the distinct values as a dropdown; `date` expects
   * an ISO value and offers a from–to range of date pickers. Defaults to `text`.
   */
  filterValue?: (row: T) => string;
  filterKind?: 'text' | 'select' | 'date';
  /** The value a cell exports to Excel; falls back to value, then filterValue, then groupValue. */
  exportValue?: (row: T) => string | number;
  /**
   * The narrowest the column may get. Long text (a store, a question) otherwise lost to the
   * header filters and wrapped to four lines a row (field test, 2 November: Aksiyon Takip).
   */
  minWidth?: number;
  /** The roll-up a numeric column opens on; defaults to sum. */
  defaultAgg?: GridAgg;
  /** How a rolled-up number is shown; defaults to the number itself. */
  formatAgg?: (n: number, agg: GridAgg) => ReactNode;
}

/** A workbook the grid lays out from what it is currently showing. */
export interface GridExport {
  fileName: string;
  sheets: { name: string; rows: (string | number)[][] }[];
}

export interface GroupableGridProps<T> {
  /** Read out before the grid; the only thing that says what it is. */
  caption: string;
  columns: GridColumn<T>[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  status: TableStatus;
  /** When set, the grouping, roll-ups and hidden columns are remembered under this key. */
  storageKey?: string;
  /** The grouping the grid opens on, as a list of column ids. Defaults to none. */
  initialGroupBy?: string[];
  /** Told the grouping and the per-column roll-ups whenever they change, so an export can match. */
  onViewChange?: (view: { groupBy: string[]; aggs: Record<string, GridAgg> }) => void;
  /** Told the rows left after the header filters, so a summary above the grid can follow them. */
  onFilteredRowsChange?: (rows: readonly T[]) => void;
  /** When set, an Excel button appears that hands back the view and the filtered flat data. */
  onExport?: (payload: GridExport) => void;
  /** The file name the export carries; defaults to report.xlsx. */
  exportFileName?: string;
  emptyState: ReactNode;
  errorState?: ReactNode;
  skeletonRows?: number;
}

interface GroupNode<T> {
  label: string;
  rows: T[];
  children: GroupNode<T>[] | null;
}

const classes = (...parts: Array<string | null | undefined | false>) =>
  parts.filter(Boolean).join(' ') || undefined;

interface SortState {
  columnId: string;
  direction: 'asc' | 'desc';
}

interface DateRange {
  from?: string;
  to?: string;
}

type FilterState = string | DateRange;

interface StoredView {
  groupBy?: string[];
  aggs?: Record<string, GridAgg>;
  hidden?: string[];
  sort?: SortState | null;
  order?: string[];
}

export function sortKey<T>(column: GridColumn<T>, row: T): string | number | null {
  if (column.sortValue) return column.sortValue(row);
  if (column.value) return column.value(row);
  if (column.groupValue) return column.groupValue(row);
  return null;
}

function sortable<T>(column: GridColumn<T>): boolean {
  return !!(column.sortValue || column.value || column.groupValue);
}

export function compareRows<T>(a: T, b: T, column: GridColumn<T>, direction: 'asc' | 'desc'): number {
  const ka = sortKey(column, a);
  const kb = sortKey(column, b);
  // Nulls sink to the bottom whichever way the column is sorted.
  if (ka === null && kb === null) return 0;
  if (ka === null) return 1;
  if (kb === null) return -1;
  const c =
    typeof ka === 'number' && typeof kb === 'number'
      ? ka - kb
      : String(ka).localeCompare(String(kb), 'tr');
  return direction === 'asc' ? c : -c;
}

function loadView(key?: string): StoredView | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as StoredView) : null;
  } catch {
    return null;
  }
}

function tree<T>(rows: readonly T[], cols: GridColumn<T>[], level: number): GroupNode<T>[] | null {
  if (level >= cols.length) return null;
  const by = cols[level]!.groupValue!;
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const key = by(row);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(row);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], 'tr'))
    .map(([label, groupRows]) => ({
      label,
      rows: groupRows,
      children: tree(groupRows, cols, level + 1),
    }));
}

export function rollUp<T>(column: GridColumn<T>, rows: readonly T[], agg: GridAgg): number | null {
  if (agg === 'count') return rows.length;
  const nums = rows.map((r) => column.value!(r)).filter((n): n is number => n !== null);
  if (nums.length === 0) return null;
  const sum = nums.reduce((s, n) => s + n, 0);
  return agg === 'avg' ? Math.round(sum / nums.length) : sum;
}

export function GroupableGrid<T>({
  caption,
  columns,
  rows,
  getRowId,
  status,
  storageKey,
  initialGroupBy = [],
  onViewChange,
  onFilteredRowsChange,
  onExport,
  exportFileName = 'report.xlsx',
  emptyState,
  errorState,
  skeletonRows = 8,
}: GroupableGridProps<T>) {
  const t = useTranslate();

  const stored = useMemo(() => loadView(storageKey), [storageKey]);

  const [groupBy, setGroupBy] = useState<string[]>(() =>
    (stored?.groupBy ?? initialGroupBy).filter((id) => columns.some((c) => c.id === id && c.groupValue)),
  );
  const [aggs, setAggs] = useState<Record<string, GridAgg>>(() => ({
    ...Object.fromEntries(columns.filter((c) => c.value).map((c) => [c.id, c.defaultAgg ?? 'sum'])),
    ...(stored?.aggs ?? {}),
  }));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(stored?.hidden ?? []));
  const [sort, setSort] = useState<SortState | null>(() => stored?.sort ?? null);
  const [order, setOrder] = useState<string[]>(() => stored?.order ?? columns.map((c) => c.id));
  const [filters, setFilters] = useState<Record<string, FilterState>>({});
  const [over, setOver] = useState(false);

  // Remembered per the key the screen gives (which carries the user), so the layout a reader
  // arranged is the one they come back to. Browser-only for now; a server-side preference can
  // replace this later without the grid changing.
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ groupBy, aggs, hidden: [...hidden], sort, order }),
      );
    } catch {
      // A private window or blocked storage: the layout just will not persist.
    }
  }, [storageKey, groupBy, aggs, hidden, sort, order]);

  function changeGroupBy(next: string[]) {
    setGroupBy(next);
    setCollapsed(new Set());
    onViewChange?.({ groupBy: next, aggs });
  }
  function changeAgg(id: string, agg: GridAgg) {
    const next = { ...aggs, [id]: agg };
    setAggs(next);
    onViewChange?.({ groupBy, aggs: next });
  }

  const groupCols = useMemo(
    () => groupBy.map((id) => columns.find((c) => c.id === id)!).filter(Boolean),
    [groupBy, columns],
  );
  // Columns in the reader's chosen order, with any the code added since kept at the end.
  const ordered = useMemo(() => {
    const known = new Map(columns.map((c) => [c.id, c] as const));
    const seen = new Set<string>();
    const list: GridColumn<T>[] = [];
    for (const id of order) {
      const c = known.get(id);
      if (c && !seen.has(id)) {
        list.push(c);
        seen.add(id);
      }
    }
    for (const c of columns) if (!seen.has(c.id)) list.push(c);
    return list;
  }, [order, columns]);

  // A grouped column leaves the grid — its value is the group it is folded under — and so does
  // one the reader has hidden from the column chooser.
  const visible = useMemo(
    () => ordered.filter((c) => !groupBy.includes(c.id) && !hidden.has(c.id)),
    [ordered, groupBy, hidden],
  );

  function moveColumn(dragId: string, targetId: string) {
    if (dragId === targetId) return;
    const base = ordered.map((c) => c.id).filter((id) => id !== dragId);
    const at = base.indexOf(targetId);
    base.splice(at < 0 ? base.length : at, 0, dragId);
    setOrder(base);
  }

  function toggleHidden(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      // Hiding is allowed down to one column, never to none.
      if (!next.has(id) && visible.length <= 1) return prev;
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) =>
      typeof v === 'string' ? v.trim() !== '' : !!(v.from || v.to),
    );
    if (active.length === 0) return rows;
    return rows.filter((row) =>
      active.every(([id, f]) => {
        const column = columns.find((c) => c.id === id);
        if (!column?.filterValue) return true;
        const v = column.filterValue(row);
        if (column.filterKind === 'date' && typeof f !== 'string') {
          return (!f.from || v >= f.from) && (!f.to || v <= f.to);
        }
        if (column.filterKind === 'select') {
          return f === '' || v === f;
        }
        return v.toLocaleLowerCase('tr').includes(String(f).trim().toLocaleLowerCase('tr'));
      }),
    );
  }, [rows, filters, columns]);

  // The distinct values a `select` filter offers — only those actually present in the data.
  const selectOptions = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const column of columns) {
      if (column.filterKind === 'select' && column.filterValue) {
        const set = new Set<string>();
        for (const row of rows) {
          const v = column.filterValue(row);
          if (v) set.add(v);
        }
        out[column.id] = [...set].sort((a, b) => a.localeCompare(b, 'tr'));
      }
    }
    return out;
  }, [columns, rows]);

  // The screen above the grid keeps its summary over the same rows the filters leave.
  const notify = useRef(onFilteredRowsChange);
  notify.current = onFilteredRowsChange;
  useEffect(() => {
    notify.current?.(filtered);
  }, [filtered]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.id === sort.columnId);
    if (!column) return filtered;
    return [...filtered].sort((a, b) => compareRows(a, b, column, sort.direction));
  }, [filtered, sort, columns]);

  const root = useMemo(() => tree(sorted, groupCols, 0), [sorted, groupCols]);

  function toggleSort(id: string) {
    setSort((prev) =>
      prev?.columnId !== id
        ? { columnId: id, direction: 'asc' }
        : prev.direction === 'asc'
          ? { columnId: id, direction: 'desc' }
          : null,
    );
  }

  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });

  function drop(e: DragEvent) {
    e.preventDefault();
    setOver(false);
    const id = e.dataTransfer.getData('text/plain');
    if (id && columns.some((c) => c.id === id && c.groupValue) && !groupBy.includes(id)) {
      changeGroupBy([...groupBy, id]);
    }
  }

  const aggCell = (column: GridColumn<T>, groupRows: readonly T[]) => {
    if (!column.value) return <td key={column.id} className={classes(column.align === 'right' && 'is-numeric')} />;
    const agg = aggs[column.id] ?? 'sum';
    const n = rollUp(column, groupRows, agg);
    return (
      <td key={column.id} className="is-numeric">
        {n === null ? <span className="nv-subtle">—</span> : column.formatAgg ? column.formatAgg(n, agg) : n}
      </td>
    );
  };

  // Group and total rows show the aggregated numerics across the visible columns; the first
  // visible column carries the label instead.
  const summaryCells = (groupRows: readonly T[]) =>
    visible.slice(1).map((column) => aggCell(column, groupRows));

  const renderNodes = (nodes: GroupNode<T>[], level: number, parent: string): ReactNode =>
    nodes.map((node) => {
      const path = `${parent}/${level}:${node.label}`;
      const open = !collapsed.has(path);
      return (
        <Fragment key={path}>
          <tr className="is-group" onClick={() => toggle(path)} style={{ cursor: 'pointer' }}>
            <td style={{ paddingLeft: `calc(var(--space-4) + ${level} * var(--space-5))` }}>
              {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />} {node.label}{' '}
              <span className="nv-subtle">({node.rows.length})</span>
            </td>
            {summaryCells(node.rows)}
          </tr>
          {open
            ? node.children
              ? renderNodes(node.children, level + 1, path)
              : node.rows.map((row) => (
                  <tr key={getRowId(row)}>
                    {visible.map((column, i) => (
                      <td
                        key={column.id}
                        className={classes(column.className, column.align === 'right' && 'is-numeric')}
                        data-label={column.header}
                        style={
                          i === 0
                            ? { paddingLeft: `calc(var(--space-4) + ${level + 1} * var(--space-5))` }
                            : undefined
                        }
                      >
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))
            : null}
        </Fragment>
      );
    });

  function filterControl(column: GridColumn<T>) {
    if (!column.filterValue) return null;
    const kind = column.filterKind ?? 'text';
    const label = `${column.header} — ${t('grid.filter')}`;

    if (kind === 'select') {
      return (
        <select
          className="nv-filter-input"
          value={(filters[column.id] as string) ?? ''}
          aria-label={label}
          onChange={(e) => setFilters((f) => ({ ...f, [column.id]: e.target.value }))}
        >
          <option value="">{t('grid.all')}</option>
          {(selectOptions[column.id] ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }

    if (kind === 'date') {
      const range = (filters[column.id] as DateRange) ?? {};
      const patch = (p: DateRange) =>
        setFilters((f) => ({ ...f, [column.id]: { ...((f[column.id] as DateRange) ?? {}), ...p } }));
      return (
        <div className="nv-filter-dates">
          <input
            type="date"
            className="nv-filter-input"
            value={range.from ?? ''}
            aria-label={`${column.header} — ${t('grid.from')}`}
            onChange={(e) => patch({ from: e.target.value || undefined })}
          />
          <input
            type="date"
            className="nv-filter-input"
            value={range.to ?? ''}
            aria-label={`${column.header} — ${t('grid.to')}`}
            onChange={(e) => patch({ to: e.target.value || undefined })}
          />
        </div>
      );
    }

    return (
      <input
        className="nv-filter-input"
        type="text"
        value={(filters[column.id] as string) ?? ''}
        placeholder={t('grid.filter')}
        aria-label={label}
        onChange={(e) => setFilters((f) => ({ ...f, [column.id]: e.target.value }))}
      />
    );
  }

  function exportCell(column: GridColumn<T>, row: T): string | number {
    if (column.exportValue) return column.exportValue(row);
    if (column.value) return column.value(row) ?? '';
    if (column.filterValue) return column.filterValue(row);
    if (column.groupValue) return column.groupValue(row);
    return '';
  }

  function aggExport(column: GridColumn<T>, group: readonly T[]): string | number {
    if (!column.value) return '';
    return rollUp(column, group, aggs[column.id] ?? 'sum') ?? '';
  }

  // Two sheets: the view as it is grouped and totalled on screen, and the filtered rows flat.
  function buildExport(): GridExport {
    const cols = visible;

    const viewRows: (string | number)[][] = [cols.map((c) => c.header)];
    const walk = (nodes: GroupNode<T>[], level: number) => {
      for (const node of nodes) {
        viewRows.push(
          cols.map((c, i) =>
            i === 0 ? `${'    '.repeat(level)}${node.label} (${node.rows.length})` : aggExport(c, node.rows),
          ),
        );
        if (node.children) walk(node.children, level + 1);
        else for (const row of node.rows) viewRows.push(cols.map((c) => exportCell(c, row)));
      }
    };
    if (root) walk(root, 0);
    else for (const row of sorted) viewRows.push(cols.map((c) => exportCell(c, row)));
    viewRows.push(
      cols.map((c, i) => (i === 0 ? `${t('grid.grandTotal')} (${filtered.length})` : aggExport(c, filtered))),
    );

    const dataRows: (string | number)[][] = [
      columns.map((c) => c.header),
      ...sorted.map((row) => columns.map((c) => exportCell(c, row))),
    ];

    return {
      fileName: exportFileName,
      sheets: [
        { name: t('grid.sheetView'), rows: viewRows },
        { name: t('grid.sheetData'), rows: dataRows },
      ],
    };
  }

  const ready = status === 'ready' && rows.length > 0;

  return (
    <div className="nv-grid">
      {ready ? (
        <div className="nv-grid-toolbar">
          {onExport ? (
            <button type="button" className="nv-group-add" onClick={() => onExport(buildExport())}>
              <IconDownload size={14} /> {t('grid.excel')}
            </button>
          ) : null}
          <details className="nv-col-chooser">
            <summary className="nv-col-chooser__button">
              {t('grid.columns')} <IconChevronDown size={14} />
            </summary>
            <div className="nv-col-chooser__menu" role="group" aria-label={t('grid.columns')}>
              {columns
                .filter((c) => !groupBy.includes(c.id))
                .map((c) => (
                  <label key={c.id} className="nv-col-chooser__item">
                    <input type="checkbox" checked={!hidden.has(c.id)} onChange={() => toggleHidden(c.id)} />
                    {c.header}
                  </label>
                ))}
            </div>
          </details>
        </div>
      ) : null}

      {ready ? (
        <div
          className={classes('nv-group-bar', over && 'is-over')}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={drop}
        >
          {groupBy.length === 0 ? (
            <span className="nv-group-bar__hint">{t('grid.dropHint')}</span>
          ) : (
            groupBy.map((id, level) => (
              <span key={id} className="nv-group-chip">
                {level > 0 ? <span aria-hidden="true">›</span> : null}
                {columns.find((c) => c.id === id)?.header}
                <button
                  type="button"
                  className="nv-icon-button"
                  aria-label={t('grid.removeLevel')}
                  onClick={() => changeGroupBy(groupBy.filter((x) => x !== id))}
                >
                  <IconX size={13} />
                </button>
              </span>
            ))
          )}
        </div>
      ) : null}

      <div className="nv-table-scroll">
        <table className="nv-table">
          <caption className="nv-sr-only">{caption}</caption>
          <thead>
            <tr>
              {visible.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                  className={classes(
                    column.align === 'right' && 'is-numeric',
                    ready && 'is-grabbable',
                    sortable(column) && 'is-sortable',
                  )}
                  draggable={ready}
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', column.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData('text/plain');
                    if (id) moveColumn(id, column.id);
                  }}
                  aria-sort={
                    sort?.columnId === column.id
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : sortable(column)
                        ? 'none'
                        : undefined
                  }
                >
                  {sortable(column) ? (
                    <button type="button" onClick={() => toggleSort(column.id)}>
                      {column.header}
                      {sort?.columnId === column.id ? (
                        sort.direction === 'asc' ? (
                          <IconArrowUp size={13} />
                        ) : (
                          <IconArrowDown size={13} />
                        )
                      ) : (
                        <IconChevronsUpDown size={13} />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                  {column.value ? (
                    <select
                      className="nv-agg-select"
                      value={aggs[column.id] ?? 'sum'}
                      aria-label={t('grid.rollUp')}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => changeAgg(column.id, e.target.value as GridAgg)}
                    >
                      <option value="sum">{t('grid.aggSum')}</option>
                      <option value="avg">{t('grid.aggAvg')}</option>
                      <option value="count">{t('grid.aggCount')}</option>
                    </select>
                  ) : null}
                </th>
              ))}
            </tr>

            {ready ? (
              <tr className="nv-filter-row">
                {visible.map((column) => (
                  <th key={column.id}>{filterControl(column)}</th>
                ))}
              </tr>
            ) : null}
          </thead>

          <tbody aria-busy={status === 'loading' || undefined}>
            {status === 'loading'
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={`skeleton-${i}`} aria-hidden="true">
                    {visible.map((column) => (
                      <td key={column.id}>
                        <div className="nv-skeleton" style={{ height: 12, width: '70%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              : null}

            {status === 'error' && errorState ? (
              <tr>
                <td colSpan={visible.length} className="nv-state-cell">{errorState}</td>
              </tr>
            ) : null}

            {status === 'ready' && rows.length === 0 ? (
              <tr>
                <td colSpan={visible.length} className="nv-state-cell">{emptyState}</td>
              </tr>
            ) : null}

            {ready && filtered.length === 0 ? (
              <tr>
                <td colSpan={visible.length} className="nv-state-cell">{emptyState}</td>
              </tr>
            ) : null}

            {ready && filtered.length > 0 ? (
              <>
                {root
                  ? renderNodes(root, 0, '')
                  : sorted.map((row) => (
                      <tr key={getRowId(row)}>
                        {visible.map((column) => (
                          <td
                            key={column.id}
                            className={classes(column.className, column.align === 'right' && 'is-numeric')}
                            data-label={column.header}
                          >
                            {column.cell(row)}
                          </td>
                        ))}
                      </tr>
                    ))}

                <tr className="is-total">
                  <td>
                    {t('grid.grandTotal')} <span className="nv-subtle">({filtered.length})</span>
                  </td>
                  {summaryCells(filtered)}
                </tr>
              </>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
